/* eslint-disable @typescript-eslint/no-explicit-any */
import { Chip, Typography } from '@mui/material'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import { isEqual } from 'lodash'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import React from 'react'
import { KeywordDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { useAppSelector } from 'store/configureStore'
import { useKeywordsTranslation } from './useKeywordsTranslation'

type MaterialTagsProps = {
  selectedTokens: Record<string, KeywordDto[]>
  size?: 'small' | 'medium'
  variant?: 'default' | 'text-caption'
  showCategories?: KeywordDto['category'][]
  fallbackString?: string
}

export const BomItemMaterialTags = (
  props: {
    bomItemPointer: BomItemPointer
    activityId: string
  } & Omit<MaterialTagsProps, 'selectedTokens'>,
) => {
  const { bomItemPointer, ...rest } = props

  const materialSummary = useAppSelector(
    projectSelectors.bomItemActivityArticleSelector(
      bomItemPointer,
      props.activityId,
    ),
    isEqual,
  )

  if (!materialSummary) return null

  return <MaterialTagsComp {...rest} selectedTokens={materialSummary?.tokens} />
}

const MaterialTagsComp = (props: MaterialTagsProps) => {
  const { asArrayOfTranslatedKeywords } = useKeywordsTranslation()

  let keywordsWithTranslation = asArrayOfTranslatedKeywords(
    props.selectedTokens,
  )

  if (props.showCategories && props.showCategories.length) {
    keywordsWithTranslation = keywordsWithTranslation.filter((x) =>
      props.showCategories?.includes(x.category),
    )
  }

  if (keywordsWithTranslation.length === 0) {
    if (props.fallbackString) {
      return (
        <Typography variant="caption" component="p">
          {props.fallbackString}
        </Typography>
      )
    } else {
      return null
    }
  }

  if (props.variant === 'text-caption') {
    return (
      <Typography
        variant={'caption'}
        component={'div'}
        style={{
          cursor: 'inherit',
          display: 'flex',
          columnGap: 4,
          flexWrap: 'wrap',
          justifyContent: 'inherit',
        }}
        color="textSecondary"
        noWrap
      >
        {keywordsWithTranslation.map((x, i) => {
          return (
            <Typography
              key={x.originalKeyword}
              style={{ cursor: 'inherit' }}
              color="textSecondary"
              variant="caption"
            >
              {x.translatedString +
                (i < keywordsWithTranslation.length - 1 ? ' | ' : '')}
            </Typography>
          )
        })}
      </Typography>
    ) as any
  }

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
        rowGap: '8px',
        textAlign: 'left',
      }}
    >
      {keywordsWithTranslation.map((x) => (
        <Chip
          key={x.originalKeyword}
          label={x.translatedString}
          size={props.size || 'small'}
          style={{ cursor: 'inherit' }}
          variant="outlined"
        />
      ))}
    </div>
  ) as any
}

const MaterialTags = React.memo(MaterialTagsComp, isEqual)

export default MaterialTags
